import React, { useEffect } from 'react';
import { Box, VStack, Text, Icon, HStack, Flex, Badge, Divider, useColorModeValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Clock, BookOpen, Coffee, Users, Laptop, FileText, MessageCircle, Home, BatteryCharging, Brain, Calendar, Award, BarChart2, Lightbulb, Zap, BookMarked } from 'lucide-react';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import SlideContainer from '../../components/SlideContainer';
import { useAudio } from '../../context/AudioContext';

const MotionBox = motion(Box);

interface UserJourneySlideProps {
  captionsText?: string;
  isSpeaking?: boolean;
}

const JourneyStep: React.FC<{
  time: string;
  title: string;
  description: string;
  icon: React.ElementType;
  jimiFeature: string;
  pillar: 'resilience' | 'culture' | 'efficiency';
  index: number;
}> = ({ time, title, description, icon, jimiFeature, pillar, index }) => {
  const pillarsColors = {
    resilience: "blue.500",
    culture: "green.500",
    efficiency: "purple.500"
  };
  
  const pillarsLabels = {
    resilience: "Personal Resilience",
    culture: "Culture of Wellness",
    efficiency: "Practice Efficiency"
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15 }}
      p={{ base: 3, md: 4 }}
      borderRadius="xl"
      bg="white"
      shadow="sm"
      _hover={{ shadow: "md" }}
      borderLeft="4px solid"
      borderColor={pillarsColors[pillar]}
      mb={{ base: 3, md: 4 }}
    >
      <HStack spacing={{ base: 2, md: 4 }} align="start">
        <VStack 
          spacing={1} 
          align="center" 
          minW={{ base: "60px", md: "80px" }}
          borderRight="1px"
          borderColor="gray.200"
          pr={{ base: 2, md: 3 }}
        >
          <Text fontWeight="bold" color="gray.600" fontSize={{ base: "xs", md: "sm" }}>{time}</Text>
          <Icon as={icon} boxSize={{ base: 5, md: 6 }} color={pillarsColors[pillar]} />
        </VStack>
        
        <VStack align="start" spacing={{ base: 1, md: 2 }} flex={1}>
          <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>{title}</Text>
          <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">{description}</Text>
          
          <HStack pt={1} flexWrap="wrap">
            <Badge 
              colorScheme={pillar === 'resilience' ? 'blue' : pillar === 'culture' ? 'green' : 'purple'}
              fontSize={{ base: "2xs", md: "xs" }}
              mb={{ base: 1, md: 0 }}
            >
              {pillarsLabels[pillar]}
            </Badge>
            <Text 
              fontSize={{ base: "2xs", md: "xs" }} 
              fontWeight="bold" 
              color={pillarsColors[pillar]}
              ml={{ base: 0, md: 2 }}
            >
              JIMI Feature: {jimiFeature}
            </Text>
          </HStack>
        </VStack>
      </HStack>
    </MotionBox>
  );
};

const UserJourneySlide: React.FC<UserJourneySlideProps> = ({
  captionsText = '',
  isSpeaking = false
}) => {
  const journeySteps = [
    {
      time: "7:45 AM",
      title: "Morning Wellness Check-in",
      description: "Dr. Martinez begins his day with a quick wellness assessment through JIMI's AI Companion, which provides personalized mindfulness exercises to prepare for the workday ahead.",
      icon: BatteryCharging,
      jimiFeature: "AI Companion",
      pillar: "resilience" as const
    },
    {
      time: "8:30 AM",
      title: "Professional Development",
      description: "Dr. Martinez completes a 15-minute micro-learning module on communication strategies, part of his personalized growth plan created by JIMI's learning algorithm.",
      icon: BookOpen,
      jimiFeature: "Learning Pathways",
      pillar: "efficiency" as const
    },
    {
      time: "10:15 AM",
      title: "Practice Efficiency Review",
      description: "During a break, Dr. Martinez reviews his practice efficiency dashboard, which highlights opportunities for workflow improvements and time management.",
      icon: BarChart2,
      jimiFeature: "Practice Analytics",
      pillar: "efficiency" as const
    },
    {
      time: "12:00 PM",
      title: "Lunch Break Optimization",
      description: "JIMI reminds Dr. Martinez to take his full lunch break and suggests a quick guided meditation to recharge before afternoon activities.",
      icon: Coffee,
      jimiFeature: "Smart Guidance",
      pillar: "resilience" as const
    },
    {
      time: "2:30 PM",
      title: "Knowledge Enhancement",
      description: "Dr. Martinez accesses JIMI's knowledge repository to review Houston Methodist's latest practice guidelines and evidence-based recommendations.",
      icon: BookMarked,
      jimiFeature: "Knowledge Repository",
      pillar: "efficiency" as const
    },
    {
      time: "4:15 PM",
      title: "Peer Mentorship Session",
      description: "Dr. Martinez participates in a virtual mentorship session with a senior colleague, facilitated by JIMI's peer matching algorithm.",
      icon: Users,
      jimiFeature: "Peer Network",
      pillar: "culture" as const
    },
    {
      time: "5:45 PM",
      title: "Reflection and Documentation",
      description: "Using JIMI's reflection tools, Dr. Martinez documents his professional growth achievements for the day and sets intentions for tomorrow.",
      icon: FileText,
      jimiFeature: "Progress Tracking",
      pillar: "resilience" as const
    },
    {
      time: "7:00 PM",
      title: "Community of Practice",
      description: "At home, Dr. Martinez joins a virtual community discussion on practice innovations across Houston Methodist, sharing ideas with colleagues.",
      icon: Lightbulb,
      jimiFeature: "Community Engagement",
      pillar: "culture" as const
    }
  ];

  // Get audio playback controls
  const { playAudioForSlide } = useAudio();

  // Play audio when component mounts
  useEffect(() => {
    // Play audio with a slight delay to ensure DOM is ready
    const audioTimer = setTimeout(() => {
      console.log('[UserJourneySlide] Starting audio playback...');
      playAudioForSlide('user-journey');
    }, 800);
    
    // Cleanup function
    return () => {
      clearTimeout(audioTimer);
      // Audio cleanup is handled by AudioContext
    };
  }, [playAudioForSlide]);

  return (
    <MethodistSlide 
      title="A Day with JIMI: The Wellness Journey" 
      description="Experience how JIMI seamlessly integrates into a Houston Methodist physician's daily workflow, enhancing professional fulfillment and practice efficiency."
    >
      <SlideContainer 
        captionsText={captionsText} 
        isSpeaking={isSpeaking}
        hudStyle="dark"
      >
        <VStack spacing={{ base: 3, md: 4 }} align="stretch">
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            p={{ base: 3, md: 4 }}
            borderRadius="xl"
            bg="methodist.blue.50"
            mb={{ base: 1, md: 2 }}
          >
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              fontStyle="italic" 
              color="methodist.blue.700"
            >
              Follow Dr. Martinez, a Family Medicine physician at Houston Methodist Primary Care, through his workday with JIMI. 
              See how each interaction with the platform supports professional development, wellness, and practice management.
            </Text>
          </MotionBox>
          
          {journeySteps.map((step, index) => (
            <JourneyStep
              key={index}
              time={step.time}
              title={step.title}
              description={step.description}
              icon={step.icon}
              jimiFeature={step.jimiFeature}
              pillar={step.pillar}
              index={index}
            />
          ))}
          
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            p={{ base: 3, md: 4 }}
            borderRadius="lg"
            bg="methodist.blue"
            color="white"
          >
            <HStack 
              justify="space-around" 
              flexDir={{ base: "column", sm: "row" }}
              spacing={{ base: 3, md: 0 }}
              align="center"
            >
              <VStack>
                <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">8+</Text>
                <Text fontSize={{ base: "xs", md: "sm" }}>Daily Touchpoints</Text>
              </VStack>
              <VStack>
                <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">3</Text>
                <Text fontSize={{ base: "xs", md: "sm" }}>Fulfillment Pillars</Text>
              </VStack>
              <VStack>
                <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">1</Text>
                <Text fontSize={{ base: "xs", md: "sm" }}>Integrated Platform</Text>
              </VStack>
            </HStack>
          </MotionBox>
        </VStack>
      </SlideContainer>
    </MethodistSlide>
  );
};

export default UserJourneySlide;
